/**
 * Progress strips (ATC-SIM): light blue for departures, buff for arrivals. Callsign,
 * assigned heading or fix, assigned altitude with a climb/descent arrow, type, and the
 * requested fix or "Arrival", and what the pilot is waiting for. Clicking a strip selects the aircraft.
 *
 * Laid out as a strip board rather than one list: an arrivals bay, a departures bay and an
 * overflights bay that is hidden while empty. An aircraft never moves between bays, so the board
 * reads the same from one minute to the next; urgency moves it up inside its own bay.
 */
import { distanceM, findRunwayEnd, NM_TO_M, type AircraftState, type AirportData } from '@atc/core';

export interface Strips {
  update(states: AircraftState[]): void;
  setSelected(callsign: string | null): void;
  on(event: 'select', handler: (callsign: string) => void): () => void;
  destroy(): void;
}

/** What a strip shows for aircraft on the ground: holding, line up and wait, rolling, stopping, landed. */
const GROUND_STATE: Record<string, string> = { holdShort: 'HOLD', lineUp: 'LUAW', linedUp: 'LUAW', takeoffRoll: 'ROLL', abortRoll: 'STOP', rollout: 'LAND', landed: 'LAND' };

type Bay = 'arrival' | 'departure' | 'overflight' | 'runway' | 'transfer';
const BAYS: readonly { kind: Bay; title: string }[] = [
  { kind: 'arrival', title: 'Arrivals' },
  { kind: 'departure', title: 'Departures' },
  { kind: 'overflight', title: 'Overflights' },
  { kind: 'runway', title: 'Runway activity' },
  { kind: 'transfer', title: 'Transfers pending' },
];
const DONE_PHASES = new Set(['handedOff', 'exited', 'cancelled', 'forcedLanding', 'landed']);
/** Where a departure is in the queue for the runway; the one rolling is at the head. */
const GROUND_RANK: Record<string, number> = { takeoffRoll: 0, abortRoll: 0, linedUp: 1, lineUp: 1, holdShort: 2 };

/**
 * How far up its bay a strip sits. The thing that needs you is always at the top; below that,
 * arrivals run in landing order and departures in runway-queue order.
 * Assumption: distance is rounded to half a mile and altitude to 500 ft, so ordinary flying does
 * not shuffle the board every quarter-second.
 */
function sortKey(s: AircraftState, airport: AirportData): [number, number, string] {
  const distress = s.emergency?.level === 'mayday' || s.fuelState === 'mayday';
  const bucket = DONE_PHASES.has(s.phase ?? '') ? 7
    : distress ? 0
      : s.conflict ? 1
        : s.waiting ? (s.waiting.stage >= 2 ? 2 : s.waiting.stage === 1 ? 3 : 4)
          : s.conflictWarning ? 5 : 6;
  let within = 0;
  if (s.kind === 'departure') {
    const ground = GROUND_RANK[s.phase ?? ''];
    // Still on the ground: queue order. Airborne: lowest first, so the near-handoff ones sink.
    within = ground !== undefined ? ground : 10 + Math.round(s.altitudeFt / 500);
  } else {
    const rw = findRunwayEnd(airport, s.assigned?.runway ?? airport.arrivalRunway);
    within = rw ? Math.round((distanceM(s.lat, s.lon, rw.lat, rw.lon) / NM_TO_M) * 2) : 0;
  }
  return [bucket, within, s.callsign];
}

const compare = (a: [number, number, string], b: [number, number, string]) => a[0] - b[0] || a[1] - b[1] || a[2].localeCompare(b[2]);

/** The board as callsigns, one list per bay, in the order the strips are stacked. Pure; no DOM. */
export function stripBays(states: readonly AircraftState[], airport: AirportData): { kind: Bay; callsigns: string[] }[] {
  const keyed = new Map<Bay, { callsign: string; key: [number, number, string] }[]>(BAYS.map((b) => [b.kind, []]));
  const tower = states.some((s) => s.control);
  for (const s of states) {
    const bay: Bay = s.control?.offeredTo ? 'transfer' : s.control && ['lineUp', 'linedUp', 'takeoffRoll', 'abortRoll', 'rollout', 'vacating'].includes(s.phase ?? '') ? 'runway' : (s.kind ?? 'arrival') as Bay;
    keyed.get(bay)!.push({ callsign: s.callsign, key: sortKey(s, airport) });
  }
  return BAYS.filter(({ kind }) => tower || !['runway', 'transfer'].includes(kind)).map(({ kind }) => ({ kind, callsigns: keyed.get(kind)!.sort((a, b) => compare(a.key, b.key)).map((x) => x.callsign) }));
}

export function createStrips(container: HTMLElement, airport: AirportData): Strips {
  container.classList.add('rs-strips');
  const rows = new Map<string, HTMLButtonElement>();
  const handlers = new Set<(cs: string) => void>();
  let selected: string | null = null;
  let hovered = false;

  // Hold the order still under the pointer so a strip never slides out from under a click.
  container.addEventListener('pointerenter', () => { hovered = true; });
  container.addEventListener('pointerleave', () => { hovered = false; });

  const bays = new Map<Bay, { el: HTMLElement; count: HTMLElement }>();
  for (const { kind, title } of BAYS) {
    const el = document.createElement('div');
    el.className = `rs-bay rs-bay-${kind}`;
    el.hidden = true;
    const head = document.createElement('h3');
    head.className = 'rs-bay-title';
    head.innerHTML = `<span>${title}</span><b></b>`;
    el.appendChild(head);
    container.appendChild(el);
    bays.set(kind, { el, count: head.querySelector('b')! });
  }

  container.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest('button');
    if (b?.dataset.cs) for (const h of handlers) h(b.dataset.cs);
  });

  function render(row: HTMLButtonElement, s: AircraftState): void {
    const a = s.assigned ?? {};
    const ground = s.onGround ? GROUND_STATE[s.phase ?? ''] : undefined;
    const landed = s.phase === 'rollout' || s.phase === 'landed';
    const nav = ground ?? (a.hold ? `H ${a.hold}` : a.fix ? a.fix : a.headingDeg !== undefined ? String(Math.round(a.headingDeg)).padStart(3, '0') : '—');
    const arrow = a.altitudeFt === undefined || s.onGround ? '' : s.altitudeFt < a.altitudeFt - 100 ? '↑' : s.altitudeFt > a.altitudeFt + 100 ? '↓' : '';
    const alt = a.altitudeFt !== undefined && !landed ? `${Math.round(a.altitudeFt / 100)}${arrow}` : '—';
    const rw = a.runway ? ` L${a.runway}` : '';
    // A declared emergency replaces the "waiting for you" code: it is the only thing that matters on that strip.
    const trouble = s.emergency ? `${s.emergency.level === 'mayday' ? 'MAYDAY' : 'PAN'} ${s.emergency.code}` : s.fuelState === 'mayday' ? 'MAYDAY FUEL' : s.fuelState === 'minimum' ? 'MIN FUEL' : '';
    const distress = !!trouble && (s.emergency?.level === 'mayday' || s.fuelState === 'mayday');
    const last = trouble ? `<i class="rs-emerg" title="declared emergency">${trouble}</i>` : s.waiting ? `<i class="rs-wait" title="pilot waiting for you">${s.waiting.need}</i>` : '';
    const responsibility = s.control ? `${s.control.owner}${s.control.offeredTo ? ` → ${s.control.offeredTo}` : ''}` : s.requested ?? '';
    row.innerHTML = `<b>${s.callsign}${s.pending ? '<i class="rs-pending" title="acknowledged, pilot not yet complying">…</i>' : ''}</b><span>${nav}</span><span>${alt}${rw}</span><span>${s.type}</span><span>${last}${responsibility}</span>`;
    row.className = `rs-strip ${s.kind ?? 'arrival'}${DONE_PHASES.has(s.phase ?? '') ? ' inactive' : ''}${s.conflict ? ' conflict' : s.conflictWarning ? ' warning' : ''}${s.waiting ? ` waiting stage-${Math.min(2, s.waiting.stage)}` : ''}${trouble ? (distress ? ' emergency' : ' minfuel') : ''}${s.callsign === selected ? ' selected' : ''}`;
  }

  return {
    update(states) {
      const seen = new Set<string>();
      for (const s of states) {
        seen.add(s.callsign);
        let row = rows.get(s.callsign);
        if (!row) { row = document.createElement('button'); row.dataset.cs = s.callsign; rows.set(s.callsign, row); }
        render(row, s);
      }
      for (const [cs, row] of rows) if (!seen.has(cs)) { row.remove(); rows.delete(cs); }
      for (const bay of bays.values()) bay.el.hidden = true;
      for (const { kind, callsigns } of stripBays(states, airport)) {
        const bay = bays.get(kind)!;
        bay.el.hidden = callsigns.length === 0;
        bay.count.textContent = String(callsigns.length);
        const list = callsigns.map((cs) => rows.get(cs)!);
        // Under the pointer the order is frozen, unless a new strip still has to be attached.
        if (hovered && list.every((row) => row.parentElement === bay.el)) continue;
        // A row already in the right place costs nothing; insertBefore only runs where the order moved.
        list.forEach((row, i) => { if (bay.el.children[i + 1] !== row) bay.el.insertBefore(row, bay.el.children[i + 1] ?? null); });
      }
    },
    setSelected(cs) { selected = cs; for (const [k, row] of rows) row.classList.toggle('selected', k === cs); },
    on(_e, h) { handlers.add(h); return () => handlers.delete(h); },
    destroy() { container.replaceChildren(); container.classList.remove('rs-strips'); bays.clear(); },
  };
}
